import { i18n } from '@/i18n';

// Dental domain values shared by the chart, the parser and the API layer.
// Must stay in step with docs/API.md.

/** Conventional reading order: M-O-D, then B, L. */
export const SURFACES = ['M', 'O', 'I', 'D', 'B', 'L'] as const;
export type Surface = (typeof SURFACES)[number];

export const CODES = [
  'caries',
  'filling',
  'inlay',
  'sealant',
  'fracture',
  'crown',
  'veneer',
  'root_canal',
  'periapical',
  'root_remnant',
  'devital',
  'extraction',
  'missing',
  'implant',
  'bridge_abutment',
  'bridge_pontic',
  'mobility',
  'calculus',
  'impacted',
  'wear',
  'sound',
] as const;
export type Code = (typeof CODES)[number];

export const MATERIALS = [
  'composite',
  'amalgam',
  'glass_ionomer',
  'zirconia',
  'metal_ceramic',
  'ceramic',
  'lithium_disilicate',
  'gold',
  'titanium',
  'temporary',
] as const;
export type Material = (typeof MATERIALS)[number];

export function codeLabel(code: Code): string {
  return i18n.t(`domain:codes.${code}`);
}

export function materialLabel(material: Material): string {
  return i18n.t(`domain:materials.${material}`);
}

export function surfaceLabel(surface: Surface): string {
  return i18n.t(`domain:surfaces.${surface}`);
}

/** Codes that are recorded per surface. */
export const SURFACE_CODES: ReadonlySet<Code> = new Set([
  'caries',
  'filling',
  'inlay',
  'sealant',
  'fracture',
  'wear',
]);

/** Codes that carry a material. */
export const MATERIAL_CODES: ReadonlySet<Code> = new Set([
  'filling',
  'inlay',
  'crown',
  'veneer',
  'implant',
  'bridge_abutment',
  'bridge_pontic',
]);

/** Findings describe a condition; everything else is a procedure or restoration. */
export const FINDING_CODES: ReadonlySet<Code> = new Set([
  'caries',
  'fracture',
  'periapical',
  'root_remnant',
  'devital',
  'missing',
  'mobility',
  'calculus',
  'impacted',
  'wear',
  'sound',
]);

/** Planned work is marked by a note prefix, in the language it was recorded in. */
const PLANNED_PREFIXES = ['Tervezett', 'Planirano', 'Planned', 'Planlagt'];

export function plannedPrefix(): string {
  return i18n.t('domain:planned');
}

// ── FDI numbering ────────────────────────────────────────────

export const UPPER_PERMANENT = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
export const LOWER_PERMANENT = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];
export const UPPER_PRIMARY = [55, 54, 53, 52, 51, 61, 62, 63, 64, 65];
export const LOWER_PRIMARY = [85, 84, 83, 82, 81, 71, 72, 73, 74, 75];

const ALL_TEETH = new Set([
  ...UPPER_PERMANENT,
  ...LOWER_PERMANENT,
  ...UPPER_PRIMARY,
  ...LOWER_PRIMARY,
]);

export function isValidTooth(n: number): boolean {
  return ALL_TEETH.has(n);
}

/**
 * "16, 17" or "16 17" → [16, 17] (sorted, unique); null when something is not
 * an FDI tooth number. An empty text is no teeth.
 */
export function parseTeethList(text: string): number[] | null {
  const parts = text.split(/[\s,;]+/).filter(Boolean);
  const teeth = parts.map(Number);
  if (!teeth.every(isValidTooth)) return null;
  return [...new Set(teeth)].sort((a, b) => a - b);
}

export function quadrantOf(tooth: number): number {
  return Math.floor(tooth / 10);
}

export function positionOf(tooth: number): number {
  return tooth % 10;
}

export function isPrimary(tooth: number): boolean {
  return quadrantOf(tooth) >= 5;
}

export function isUpper(tooth: number): boolean {
  const q = quadrantOf(tooth);
  return q === 1 || q === 2 || q === 5 || q === 6;
}

/** Incisors and canines have an incisal edge instead of an occlusal surface. */
export function isAnterior(tooth: number): boolean {
  return positionOf(tooth) <= 3;
}

/** Teeth on the patient's right side (shown on the viewer's left). */
export function isPatientRight(tooth: number): boolean {
  const q = quadrantOf(tooth);
  return q === 1 || q === 4 || q === 5 || q === 8;
}

/** O ↔ I depending on whether the tooth is anterior. */
export function normalizeSurfaces(tooth: number, surfaces: readonly Surface[]): Surface[] {
  const anterior = isAnterior(tooth);
  const out = new Set<Surface>();
  for (const s of surfaces) {
    if (s === 'O' && anterior) out.add('I');
    else if (s === 'I' && !anterior) out.add('O');
    else out.add(s);
  }
  return orderSurfaces([...out]);
}

/** Clinical writing order: MO, DO, MOD — proximal first, occlusal between M and D. */
export function orderSurfaces(surfaces: readonly Surface[]): Surface[] {
  const has = new Set(surfaces);
  const centre = (['O', 'I'] as const).filter((s) => has.has(s));
  const core: Surface[] =
    has.has('M') && has.has('D')
      ? ['M', ...centre, 'D']
      : [...(['M', 'D'] as const).filter((s) => has.has(s)), ...centre];
  return [...core, ...(['B', 'L'] as const).filter((s) => has.has(s))];
}

/** Teeth from `a` to `b` inclusive, in chart order, if both are in the same arch. */
export function toothRange(a: number, b: number): number[] | null {
  for (const arch of [UPPER_PERMANENT, LOWER_PERMANENT, UPPER_PRIMARY, LOWER_PRIMARY]) {
    const ia = arch.indexOf(a);
    const ib = arch.indexOf(b);
    if (ia >= 0 && ib >= 0) {
      return ia <= ib ? arch.slice(ia, ib + 1) : arch.slice(ib, ia + 1);
    }
  }
  return null;
}

export function describeSurfaces(surfaces: readonly Surface[]): string {
  return surfaces.join('');
}

export function describeEntry(e: {
  tooth: number;
  code: Code;
  surfaces: readonly Surface[];
  material: Material | null;
  note?: string | null;
}): string {
  const parts = [`${e.tooth}`, codeLabel(e.code)];
  if (e.surfaces.length) parts.push(describeSurfaces(e.surfaces));
  if (e.material) parts.push(materialLabel(e.material).toLowerCase());
  return parts.join(' · ');
}

export function isPlanned(note: string | null | undefined): boolean {
  return !!note && PLANNED_PREFIXES.some((p) => note.startsWith(p));
}

/** Strip a planned prefix in any language: "Tervezett · mély" → "mély". */
export function stripPlanned(note: string): string {
  const prefix = PLANNED_PREFIXES.find((p) => note.startsWith(p));
  return prefix ? note.slice(prefix.length).replace(/^\s*·\s*/, '') : note;
}

/** "felső jobb első nagyőrlő" / "gornji desni prvi molar" for 16. */
export function toothName(tooth: number): string {
  return i18n.t('domain:tooth.name', {
    vertical: i18n.t(isUpper(tooth) ? 'domain:tooth.upper' : 'domain:tooth.lower'),
    side: i18n.t(isPatientRight(tooth) ? 'domain:tooth.right' : 'domain:tooth.left'),
    type: i18n.t(`domain:tooth.${isPrimary(tooth) ? 'primary' : 'permanent'}.${positionOf(tooth)}`),
  });
}
